import { STATUS_HUE } from "@/lib/statusColors";
import type { DocPath } from "@/lib/api";

/* ════════════════════════════════════════════════════════════════════════
   Formatting + types shared across the document hub
   ════════════════════════════════════════════════════════════════════════ */

export function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Exact date + time, e.g. "18 Jul 2026, 22:41". */
export function fmtDateTime(iso: string | Date): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** "in 12 days" / "3 hours ago" — used for trash countdowns. */
export function fmtRelative(iso: string | Date): string {
  const ms = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(ms);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["day", 86_400_000],
    ["hour", 3_600_000],
    ["minute", 60_000],
  ];
  for (const [unit, size] of units) {
    if (abs >= size) return rtf.format(Math.round(ms / size), unit);
  }
  return rtf.format(Math.round(ms / 60_000), "minute");
}

export function fileIcon(mime: string, name: string): { icon: string; color: string } {
  const n = name.toLowerCase();
  if (mime.includes("pdf") || n.endsWith(".pdf"))
    return { icon: "fa-file-pdf", color: STATUS_HUE.red };
  if (mime.includes("word") || n.endsWith(".docx") || n.endsWith(".doc"))
    return { icon: "fa-file-word", color: STATUS_HUE.blue };
  if (mime.includes("sheet") || n.endsWith(".xlsx") || n.endsWith(".csv"))
    return { icon: "fa-file-excel", color: STATUS_HUE.green };
  if (mime.includes("presentation") || n.endsWith(".pptx") || n.endsWith(".ppt"))
    return { icon: "fa-file-powerpoint", color: STATUS_HUE.orange };
  if (mime.startsWith("image/")) return { icon: "fa-file-image", color: STATUS_HUE.purple };
  if (n.endsWith(".md") || mime.startsWith("text/"))
    return { icon: "fa-file-lines", color: STATUS_HUE.neutral };
  return { icon: "fa-file", color: STATUS_HUE.neutral };
}

/** Insert " (n)" before a file extension: report.pdf → report (1).pdf */
export function suffixName(name: string, n: number): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? `${name.slice(0, dot)} (${n})${name.slice(dot)}` : `${name} (${n})`;
}

export type SortKey = "name" | "size" | "added" | "edited";
export type SortDir = "asc" | "desc";
export type MoveItem = { kind: "file" | "folder"; id: string; name: string };

/** Selection/cursor key for a row — the two listings share one keyspace. */
export const rowKey = (kind: "file" | "folder", id: string) => `${kind}:${id}`;

/* ════════════════════════════════════════════════════════════════════════
   Small shared UI
   ════════════════════════════════════════════════════════════════════════ */

/** "Home / Reports / 2026" under a search or recents result. */
export function PathTrail({
  path,
  onNavigate,
}: {
  path: DocPath;
  onNavigate: (folderId: string | null) => void;
}) {
  return (
    <span className="flex min-w-0 items-center gap-1 text-[0.7rem] text-text-muted">
      <button
        onClick={(e) => {
          e.stopPropagation();
          onNavigate(null);
        }}
        className="hover:text-text-secondary"
      >
        <i className="fa-solid fa-house text-[0.6rem]" />
      </button>
      {path.map((c) => (
        <span key={c.id} className="flex min-w-0 items-center gap-1">
          <i className="fa-solid fa-chevron-right text-[0.45rem]" />
          <button
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(c.id);
            }}
            className="truncate hover:text-text-secondary"
          >
            {c.name}
          </button>
        </span>
      ))}
    </span>
  );
}

export function EmptyState({
  icon,
  title,
  body,
  onUpload,
}: {
  icon: string;
  title: string;
  body: string;
  onUpload?: () => void;
}) {
  return (
    <div className="grid h-full min-h-[300px] place-items-center text-center">
      <div className="max-w-sm">
        <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-token-lg bg-accent-muted text-2xl text-accent">
          <i className={`fa-solid ${icon}`} />
        </div>
        <h2 className="font-display text-lg font-semibold text-text-heading">{title}</h2>
        <p className="mx-auto mt-1.5 text-sm text-text-secondary">{body}</p>
        {onUpload && (
          <button
            onClick={onUpload}
            className="mt-4 rounded-md px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90"
            style={{ background: "var(--color-accent)", color: "var(--color-on-accent)" }}
          >
            <i className="fa-solid fa-arrow-up-from-bracket mr-1.5 text-xs" />
            Upload files
          </button>
        )}
      </div>
    </div>
  );
}

/** Placeholder rows that hold the table's shape while the folder loads. */
export function SkeletonRows({ rows = 8 }: { rows?: number }) {
  return (
    <div className="animate-pulse">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-4 border-b border-border-light px-4 py-3"
        >
          <div className="h-4 w-4 rounded bg-bg-elevated" />
          <div className="h-3 flex-1 rounded bg-bg-elevated" style={{ maxWidth: `${40 + ((i * 13) % 35)}%` }} />
          <div className="h-3 w-16 rounded bg-bg-elevated" />
          <div className="h-3 w-24 rounded bg-bg-elevated" />
        </div>
      ))}
    </div>
  );
}
